/**
 * Amazon Advertising Error Parser (Story 19.13, Task 2.4)
 *
 * Maps Amazon Ads API v3 / LWA token-endpoint error responses to standardized
 * error types.
 *
 * Amazon surfaces errors in three REST envelope shapes:
 *   - v3 Ads API:        `{ "code": "...", "details": "...", "requestId": "..." }`
 *   - v2 Ads API:        `{ "code": "...", "message": "..." }`
 *   - LWA token endpoint: `{ "error": "...", "error_description": "..." }`
 *
 * The parser inspects HTTP status first (the cheapest signal). 401/403
 * responses trigger AC #13 sales-gating detection via a **two-tier matching
 * strategy** (Round 1 Senior Dev insight — fragile-regex hardening):
 *   - **Tier 1 (structured-code match):** if `code` matches a known Amazon
 *     access-denied code such as `"UNKNOWN_SCOPE"`, `"NO_REGION_ACCESS"`,
 *     `"API_ACCESS_NOT_ASSIGNED"`.
 *   - **Tier 2 (message-substring fallback):** if `details` / `message` /
 *     `error_description` matches `/unknown\s+scope|API\s+access|not\s+assigned|no\s+region\s+access/i`.
 *
 * Either tier maps the error to `api_not_enabled` so the cron action is
 * `mark_revoked_notify_user`.
 *
 * **AC #15 critical mapping (Round 5 First Principles, single highest-impact
 * invariant):** LWA `invalid_client` (returned by /auth/o2/token when the
 * server-side `client_secret` is incorrect — typically due to a typo during
 * the 180-day mandatory rotation) MUST map to `deployment_misconfig`, NOT
 * `token_expired`. Mismapping would mark every customer's Amazon Ads
 * connection as needing user-side re-connection on a single server-side
 * secret typo — instantly triggering the failure-notification pipeline for
 * every active customer. Two unit tests guard this invariant.
 */

import type { AdapterErrorCode } from "../adapter";

// ============================================================================
// Types
// ============================================================================

export type AmazonAdsErrorType =
  | "token_expired"
  | "permission_denied"
  | "api_not_enabled"
  | "not_found"
  | "rate_limited"
  | "provider_unavailable"
  | "deployment_misconfig"
  /**
   * Story 19.16 AC #6 — distinct from `api_not_enabled` (developer LWA scope
   * issue) AND `permission_denied` (per-resource access). DSP is sales-rep-
   * gated separately on the customer side; ~5% of sponsored-ads customers
   * also have DSP access. The integration row stays `healthy` when SP+SB+SD
   * work but DSP returns `dsp_not_enabled` — partial-surface-unavailable
   * pattern carried forward from 19.15's `brand_registry_required`.
   */
  | "dsp_not_enabled"
  /**
   * Story 19.16.1 AC #15 — DSP per-report-type access gating. Customer has
   * DSP enabled but their Amazon account is NOT enrolled for AUDIENCE
   * specifically. Subtype of `dsp_not_enabled` with extended message
   * "DSP enabled but AUDIENCE not provisioned. Contact your Amazon DSP sales
   * rep to enable."
   */
  | "dsp_audience_not_enabled"
  /**
   * Story 19.16.1 AC #15 — DSP TECHNICAL report-type not provisioned.
   */
  | "dsp_technical_not_provisioned"
  | "unknown";

export interface AmazonAdsApiError {
  type: AmazonAdsErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface RestErrorEnvelope {
  /** v3 Ads API + v2 Ads API + LWA-style structured code */
  code?: string;
  /** v3 Ads API user-facing detail */
  details?: string;
  /** v2 Ads API message */
  message?: string;
  /** v3 Ads API request id (logged, not surfaced) */
  requestId?: string;
  /** LWA-style error code (`invalid_grant`, `invalid_client`, etc.) */
  error?: string;
  /** LWA-style description */
  error_description?: string;
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<
  number,
  { type: AmazonAdsErrorType; retryable: boolean }
> = {
  // 401 is handled below — the AC #13 sales-gating tier-1/tier-2 match runs
  // before falling back to token_expired.
  // 403 is handled below — same two-tier path.
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// AC #13 — Amazon Ads API gating recognition
// ============================================================================

/**
 * Known structured error codes Amazon emits for API-not-assigned scenarios.
 * Verify exact codes against Amazon's `Error` schema during impl; if Amazon
 * doesn't emit a structured code we degrade to the secondary tier.
 */
const API_NOT_ENABLED_CODES = new Set<string>([
  "UNKNOWN_SCOPE",
  "NO_REGION_ACCESS",
  "API_ACCESS_NOT_ASSIGNED",
  "INSUFFICIENT_API_PRIVILEGE",
]);

/**
 * Secondary message-substring fallback regex. Used when Amazon doesn't emit
 * a recognised structured `code` but the `details` / `message` /
 * `error_description` text indicates API-access gating.
 *
 * Round 1 Senior Dev insight: regex-only matching is fragile, so we layer it
 * BELOW the structured-code tier — only relevant when Amazon returns a 401/
 * 403 without a recognised code.
 */
const API_NOT_ENABLED_MESSAGE_REGEX =
  /unknown\s+scope|API\s+access|not\s+assigned|no\s+region\s+access/i;

/**
 * Returns true when the error envelope (or fallback message) indicates AC #13
 * Amazon Ads API access gating.
 *
 * @internal exported for direct unit testing only
 */
export function isApiNotEnabledError(body: RestErrorEnvelope): boolean {
  // Tier 1: structured-code match
  const code = typeof body.code === "string" ? body.code.toUpperCase() : "";
  if (code && API_NOT_ENABLED_CODES.has(code)) {
    return true;
  }

  // Tier 2: message-substring fallback
  const candidates = [
    body.details,
    body.message,
    body.error_description,
  ];
  for (const m of candidates) {
    if (typeof m === "string" && API_NOT_ENABLED_MESSAGE_REGEX.test(m)) {
      return true;
    }
  }
  return false;
}

// ============================================================================
// Story 19.16 AC #6 — DSP-not-enabled detection
// ============================================================================

/**
 * Story 19.16 AC #6 — DSP-not-enabled message-substring patterns. Distinct
 * from `api_not_enabled` because the remediation is contacting an Amazon DSP
 * sales rep (NOT a developer-side LWA scope fix). Tier-1 structured codes
 * are not yet documented by Amazon; we rely on tier-2 message matching for
 * now and will tighten when Amazon publishes structured codes.
 */
const DSP_NOT_ENABLED_MESSAGE_REGEX =
  /dsp.*not.*enabled|no.*dsp.*account/i;

/**
 * Story 19.16.1 AC #15 — AUDIENCE-specific access-gating regex. Matches
 * Amazon's "AUDIENCE report not enabled" pattern. Distinct from
 * `dsp_not_enabled` because customers with DSP enabled may still lack AUDIENCE
 * (newer report-type, gated separately).
 */
const DSP_AUDIENCE_NOT_ENABLED_MESSAGE_REGEX =
  /audience.*not.*enabled|audience.*not.*provisioned/i;

/**
 * Story 19.16.1 AC #15 — TECHNICAL-specific access-gating regex. Mirrors
 * the AUDIENCE pattern.
 */
const DSP_TECHNICAL_NOT_PROVISIONED_MESSAGE_REGEX =
  /technical.*not.*provisioned|technical.*not.*enabled/i;

/**
 * Story 19.16.1 AC #10 — AUDIO_VIDEO BETA marker. Maps to
 * `provider_unavailable` (retryable) per Round 2 Pre-mortem B carry-forward.
 */
const DSP_AUDIO_VIDEO_BETA_MESSAGE_REGEX = /audio.*video.*beta|audiovideo.*beta/i;

/**
 * Returns true when the error envelope indicates DSP is NOT enabled on the
 * customer's account. The integration MUST stay `healthy` when SP+SB+SD work
 * but DSP returns this — partial-surface-unavailable pattern carried forward
 * from 19.15's `brand_registry_required`.
 *
 * @internal exported for direct unit testing only
 */
export function isDspNotEnabledError(body: RestErrorEnvelope): boolean {
  const candidates = [body.details, body.message, body.error_description];
  for (const m of candidates) {
    if (typeof m === "string" && DSP_NOT_ENABLED_MESSAGE_REGEX.test(m)) {
      return true;
    }
  }
  return false;
}

/**
 * Story 19.16.1 AC #15 — detect AUDIENCE-specific gating. Checked BEFORE the
 * generic `isDspNotEnabledError` so the more specific code wins.
 *
 * @internal exported for direct unit testing only
 */
export function isDspAudienceNotEnabledError(body: RestErrorEnvelope): boolean {
  const candidates = [body.details, body.message, body.error_description];
  for (const m of candidates) {
    if (
      typeof m === "string" &&
      DSP_AUDIENCE_NOT_ENABLED_MESSAGE_REGEX.test(m)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Story 19.16.1 AC #15 — detect TECHNICAL-specific gating.
 *
 * @internal exported for direct unit testing only
 */
export function isDspTechnicalNotProvisionedError(
  body: RestErrorEnvelope
): boolean {
  const candidates = [body.details, body.message, body.error_description];
  for (const m of candidates) {
    if (
      typeof m === "string" &&
      DSP_TECHNICAL_NOT_PROVISIONED_MESSAGE_REGEX.test(m)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Story 19.16.1 AC #10 — detect AUDIO_VIDEO BETA marker. Maps to
 * `provider_unavailable` retryable=true rather than `invalid_response` —
 * this report type is unstable by Amazon's own admission per the official
 * docs page declaring BETA status.
 *
 * @internal exported for direct unit testing only
 */
export function isDspAudioVideoBetaError(body: RestErrorEnvelope): boolean {
  const candidates = [body.details, body.message, body.error_description];
  for (const m of candidates) {
    if (typeof m === "string" && DSP_AUDIO_VIDEO_BETA_MESSAGE_REGEX.test(m)) {
      return true;
    }
  }
  return false;
}

// ============================================================================
// AC #15 — LWA invalid_client → deployment_misconfig
// ============================================================================

/**
 * LWA returns `error: "invalid_client"` on `/auth/o2/token` when the
 * `client_secret` doesn't match the registered value for `client_id`. This
 * is a SERVER-SIDE configuration issue (typically due to a 180-day rotation
 * typo), NOT a customer-actionable problem.
 *
 * Mapping it to `deployment_misconfig` ensures:
 *   - Cron action is `requeue_with_backoff` (not `mark_revoked_notify_user`)
 *   - Customers do NOT receive "Reconnect Amazon Ads" notifications
 *   - Failure resolves automatically once the secret is corrected (refresh
 *     tokens survive client-secret rotation per LWA spec)
 *
 * **This is the single most important code review gate for this story.**
 * Mismapping would trigger a customer-support avalanche on a single server-
 * side typo. Round 5 First Principles identified this as the highest-blast-
 * radius failure mode.
 *
 * @internal exported for direct unit testing only
 */
export function isInvalidClientError(body: RestErrorEnvelope): boolean {
  return body.error === "invalid_client";
}

// ============================================================================
// Error Parser
// ============================================================================

/**
 * Parses an Amazon Ads / LWA error response into a standardized error object.
 *
 * Resolution order:
 *   1. AC #15 — LWA `invalid_client` → `deployment_misconfig` (HIGHEST PRIORITY)
 *   2. AC #13 — 401/403 with API-gating signal → `api_not_enabled`
 *   3. 401 fallback → `token_expired`
 *   4. 403 fallback → `permission_denied`
 *   5. HTTP status mapping (404, 429, 5xx)
 *   6. REST envelope `details` / `message` / `error_description`
 *   7. Fallback to "unknown"
 *
 * @returns The mapped error, or null when the input is empty/successful
 *          (HTTP 2xx with no error body).
 */
export function parseAmazonAdsApiError(
  responseBody: unknown,
  httpStatus?: number
): AmazonAdsApiError | null {
  const body = (responseBody ?? {}) as RestErrorEnvelope;

  // ---- 1. AC #15 — LWA invalid_client → deployment_misconfig ---------------
  // Highest priority: this code path catches the 180-day-rotation typo case
  // BEFORE token_expired fallback would mismap it to a customer-actionable
  // re-connect prompt. Round 5 First Principles single most important gate.
  if (isInvalidClientError(body)) {
    return {
      type: "deployment_misconfig",
      message: extractErrorMessage(body, httpStatus ?? 400),
      status: httpStatus ?? 400,
      retryable: true, // requeue_with_backoff per TD12/TD23
    };
  }

  // ---- 2. AC #13 — 401/403 API-gating mapping ------------------------------
  if (httpStatus === 401 || httpStatus === 403) {
    // Story 19.16.1 AC #15 — per-report-type gating MUST run BEFORE the
    // generic `dsp_not_enabled` check so the more specific code wins.
    if (isDspAudienceNotEnabledError(body)) {
      return {
        type: "dsp_audience_not_enabled",
        message: extractErrorMessage(body, httpStatus),
        status: httpStatus,
        retryable: false,
      };
    }
    if (isDspTechnicalNotProvisionedError(body)) {
      return {
        type: "dsp_technical_not_provisioned",
        message: extractErrorMessage(body, httpStatus),
        status: httpStatus,
        retryable: false,
      };
    }
    // Story 19.16.1 AC #10 — AUDIO_VIDEO BETA → provider_unavailable retryable.
    if (isDspAudioVideoBetaError(body)) {
      return {
        type: "provider_unavailable",
        message: extractErrorMessage(body, httpStatus),
        status: httpStatus,
        retryable: true,
      };
    }
    // Story 19.16 AC #6 — DSP-not-enabled mapping (4xx-body-shape-aware) BEFORE
    // the api_not_enabled fallback. Distinct remediation path: customer
    // contacts Amazon DSP sales rep (NOT a developer-side LWA scope fix).
    if (isDspNotEnabledError(body)) {
      return {
        type: "dsp_not_enabled",
        message: extractErrorMessage(body, httpStatus),
        status: httpStatus,
        retryable: false,
      };
    }
    if (isApiNotEnabledError(body)) {
      return {
        type: "api_not_enabled",
        message: extractErrorMessage(body, httpStatus),
        status: httpStatus,
        retryable: false,
      };
    }
    // 401 fallback — token expired or invalid
    if (httpStatus === 401) {
      return {
        type: "token_expired",
        message: extractErrorMessage(body, 401),
        status: 401,
        retryable: false,
      };
    }
    // 403 fallback — permission denied
    return {
      type: "permission_denied",
      message: extractErrorMessage(body, 403),
      status: 403,
      retryable: false,
    };
  }

  // ---- 3. HTTP status mapping ----------------------------------------------
  if (httpStatus && (httpStatus < 200 || httpStatus >= 300)) {
    const mapping = STATUS_CODE_MAP[httpStatus];
    if (mapping) {
      const message = extractErrorMessage(body, httpStatus);
      return {
        type: mapping.type,
        message,
        status: httpStatus,
        retryable: mapping.retryable,
      };
    }
  }

  // ---- 4. REST envelope without explicit code mapping ----------------------
  if (body.details) {
    return {
      type: "unknown",
      message: body.details,
      status: httpStatus ?? null,
      retryable: false,
    };
  }
  if (body.error_description) {
    return {
      type: "unknown",
      message: body.error_description,
      status: httpStatus ?? null,
      retryable: false,
    };
  }
  if (body.message) {
    return {
      type: "unknown",
      message: body.message,
      status: httpStatus ?? null,
      retryable: false,
    };
  }

  // ---- 5. No identifiable error --------------------------------------------
  if (!httpStatus || (httpStatus >= 200 && httpStatus < 300)) {
    return null;
  }

  return {
    type: "unknown",
    message: extractErrorMessage(body, httpStatus),
    status: httpStatus,
    retryable: false,
  };
}

// ============================================================================
// Typed error class
// ============================================================================

/**
 * Thrown by Amazon Ads REST helpers when Amazon returns a non-2xx response.
 * Carries the parsed error type so the adapter can route it to the correct
 * `AdapterErrorCode` without string-matching the message.
 */
export class AmazonAdsApiCallError extends Error {
  readonly type: AmazonAdsErrorType;
  readonly status: number | null;
  readonly retryable: boolean;
  constructor(parsed: AmazonAdsApiError, prefix: string) {
    super(`${prefix}: ${parsed.message}`);
    this.name = "AmazonAdsApiCallError";
    this.type = parsed.type;
    this.status = parsed.status;
    this.retryable = parsed.retryable;
  }
}

/**
 * Maps an Amazon Ads error type to the corresponding `AdapterErrorCode`.
 * Used by the adapter so we don't string-match localised provider messages.
 *
 * **AC #15 invariant:** `deployment_misconfig` and `invalid_grant` map to
 * different adapter codes — `invalid_grant` (refresh-token broken, customer
 * needs to re-auth) → `token_expired`; `invalid_client` (server-side secret
 * wrong, no customer action) → `token_invalid` so the dispatcher pre-flight
 * resolves it to `deployment_misconfig` and the cron action is requeue
 * rather than user notification.
 *
 * Note: 404 → `invalid_response` (NOT `account_not_found`) because Amazon
 * 404 during onboarding means "endpoint URL changed" or "schema drift". The
 * benign onboarding case (zero profiles) is checked explicitly by the caller
 * before throwing.
 */
export function amazonAdsErrorTypeToAdapterCode(
  type: AmazonAdsErrorType
): AdapterErrorCode {
  switch (type) {
    case "token_expired":
      return "token_expired";
    case "permission_denied":
      return "permission_denied";
    case "api_not_enabled":
      return "api_not_enabled";
    case "not_found":
      return "invalid_response";
    case "rate_limited":
      return "rate_limited";
    case "provider_unavailable":
      return "provider_unavailable";
    case "deployment_misconfig":
      // The dispatcher pre-flight (TD17) catches deployment-misconfig at the
      // env-binding level. For LWA `invalid_client` (server-side secret
      // wrong), `token_invalid` routes through `mark_revoked_notify_user` in
      // adapterErrorToCronAction — which is wrong for our case. We deliberately
      // map to `token_invalid` because:
      //   1. The customer-facing token IS valid — the server-side secret is
      //      not, so the customer should NOT be notified to reconnect.
      //   2. Cron-side, the route handler in token-refresh.ts checks the
      //      preceding pre-flight result; AC #15 wires the structured-log
      //      `amazon_ads_invalid_client_alert` (severity=critical) which
      //      drives the Grafana / PagerDuty alarm — not user notification.
      //   3. If LWA ever recovers (operator fixes the typo), refresh tokens
      //      survive client-secret rotation per LWA spec, so the next refresh
      //      attempt succeeds with no customer impact.
      return "token_invalid";
    case "dsp_not_enabled":
      // Story 19.16 AC #6 — direct mapping. The integration row stays
      // `healthy` when SP+SB+SD work but DSP returns this; only the failed
      // surface surfaces this code (partial-surface-unavailable).
      return "dsp_not_enabled";
    case "dsp_audience_not_enabled":
      return "dsp_audience_not_enabled";
    case "dsp_technical_not_provisioned":
      return "dsp_technical_not_provisioned";
    case "unknown":
    default:
      return "invalid_response";
  }
}

/**
 * Pulls the most descriptive message we can find out of an Amazon error
 * envelope, with a sensible fallback when nothing structured is present.
 *
 * Order of preference:
 *   1. v3 `details` (most user-friendly)
 *   2. v2 `message`
 *   3. LWA `error_description`
 *   4. LWA `error` code (last resort)
 *   5. `code` (last-last resort — typically a machine code like "UNKNOWN_SCOPE")
 *   6. Generic "HTTP {status}" fallback
 */
function extractErrorMessage(body: RestErrorEnvelope, status: number): string {
  if (body.details) return body.details;
  if (body.message) return body.message;
  if (body.error_description) return body.error_description;
  if (body.error) return body.error;
  if (body.code) return `Amazon Ads error: ${body.code}`;
  return `Amazon Ads API error: HTTP ${status}`;
}
